import React from "react";
import { Alert, BreadcrumbItem, Button, Card, CardBody, Container, ListGroup, ListGroupItem } from "reactstrap";
import { useMutation, useQuery } from "@apollo/client";
import * as Notify from "@repoflow/form-schema/notify";
import * as Loading from "@repoflow/components/loading";
import * as FoodsQL from "queries/foods";
import { formatNumber } from "components/day/summary";

export const Sidebar = () => null;

export const Indice = () => <BreadcrumbItem>Foods</BreadcrumbItem>;

// What the organization has logged before, as it was logged last. Nothing is
// added here: a food is learnt by logging it, from the chat or the form, and
// the chat reuses these numbers the next time it is named. A food remembered
// wrong is fixed by correcting an entry of it, or taken off here and learnt
// again.
export const Content = () => {
  const { data, loading, error } = useQuery(FoodsQL.list, { fetchPolicy: "cache-and-network" });
  const foods = data?.viewer?.account?.foods?.list;

  return (
    <Container className="mt-4" style={{ maxWidth: 760 }}>
      <h4 className="m-0">Foods</h4>
      <p className="text-muted small mt-1 mb-0">
        What you've logged before. The chat uses these numbers again the next time you name one.
      </p>
      <Card className="mt-3">
        <CardBody>
          {error ? (
            <Alert color={"danger"}>The foods could not be read. {error.message}</Alert>
          ) : !foods && loading ? (
            <Loading.Icon />
          ) : !foods?.length ? (
            <p className="text-muted small m-0" data-testid={"foods-empty"}>
              Nothing yet. Log a meal and it is remembered here.
            </p>
          ) : (
            <ListGroup flush data-testid={"foods-list"}>
              {foods.map((food) => (
                <Food key={food.foodId} food={food} />
              ))}
            </ListGroup>
          )}
        </CardBody>
      </Card>
    </Container>
  );
};

const Food = ({ food }) => {
  const [remove, { loading }] = useMutation(FoodsQL.remove, {
    ...FoodsQL.AFTER_REMOVE,
    onError: (error) => Notify.error(String(error)),
  });

  return (
    <ListGroupItem className="d-flex align-items-center px-0" data-testid={"foods-item"}>
      <div className="flex-grow-1 text-truncate">
        <div>
          <span data-testid={"foods-item-name"}>{food.name}</span>
          {food.serving ? <span className="text-muted small ml-2">{food.serving}</span> : null}
        </div>
        <div className="text-muted small" data-testid={"foods-item-numbers"}>
          {formatNumber(food.calories)} kcal · {formatNumber(food.protein, 1)} P / {formatNumber(food.carbs, 1)} C / {formatNumber(food.fat, 1)} F g · {formatNumber(food.fiber, 1)} g fiber
          {food.count > 1 ? ` · logged ${food.count}×` : ""}
        </div>
      </div>
      <Button
        color={"link"}
        size={"sm"}
        className="px-1 text-danger"
        onClick={() => remove({ variables: { input: { foodId: food.foodId } } })}
        disabled={loading}
        aria-label={`Forget ${food.name}`}
        data-testid={"foods-remove"}
      >
        <i className="fa fa-trash" />
      </Button>
    </ListGroupItem>
  );
};
